import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bed, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

const BedroomPicker = ({ bedrooms, onBedroomsChange }) => {
  const { t } = useTranslation();

  const bedroomOptions = [
    // Wrong i18n key (`common.actions`) used to leak through and show
    // "Actions" as the label — now using the correct `common.any` key
    // with a sensible English fallback.
    { value: null, label: t('common.any', 'Any') },
    { value: 0, label: 'Studio' },
    { value: 1, label: `1 ${t('property.bedrooms').toLowerCase()}` },
    { value: 2, label: `2 ${t('property.bedrooms').toLowerCase()}` },
    { value: 3, label: `3 ${t('property.bedrooms').toLowerCase()}` },
    { value: 4, label: `4+ ${t('property.bedrooms').toLowerCase()}` },
  ];

  const selectedLabel = bedrooms !== null && bedrooms !== undefined
    ? bedroomOptions.find((opt) => opt.value === bedrooms)?.label
    : t('search.bedrooms');

  return (
    // modal={false} + portaled PopoverContent = unclipped by parent
    // `overflow-hidden` wrapper, interactive inside a Radix Sheet on iOS.
    // See `DatePicker.jsx` and `PRD.md#Critical Gotchas` for the full
    // background.
    <Popover modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors w-full text-left"
          data-testid="bedroom-picker-trigger"
        >
          <Bed className="w-5 h-5 text-gray-500" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-gray-700">{t('search.bedrooms')}</div>
            <div className="text-sm text-gray-600 truncate">{selectedLabel}</div>
          </div>
          <ChevronDown className="w-4 h-4 text-gray-500 transition-transform data-[state=open]:rotate-180" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-0 z-[200]"
        align="start"
        sideOffset={6}
        onCloseAutoFocus={(e) => e.preventDefault()}
        data-testid="bedroom-picker-popover"
      >
        <div className="px-4 py-2 border-b border-gray-100">
          <h3 className="font-semibold text-sm text-gray-900">{t('search.bedrooms')}</h3>
        </div>
        <div className="py-2">
          {bedroomOptions.map((option) => (
            <button
              key={String(option.value)}
              onClick={() => onBedroomsChange(option.value)}
              className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                bedrooms === option.value ? 'bg-blue-50' : ''
              }`}
              data-testid={`bedroom-option-${option.value ?? 'any'}`}
            >
              <span className="text-sm text-gray-700">{option.label}</span>
              {bedrooms === option.value && (
                <div className="w-2 h-2 bg-blue-500 rounded-full" />
              )}
            </button>
          ))}
        </div>
        <div className="px-4 py-2 border-t border-gray-100">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onBedroomsChange(null)}
            className="w-full text-sm"
          >
            {t('common.clear')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default BedroomPicker;
